import { useTheme } from "@amx/ux-twoui-theming";
import { generateGuid } from "@amx/ux-twoui-utils";
import { Stack as FluentStack } from "@fluentui/react";
import React, { createContext } from "react";
import { BaseProps } from "../../../shared/props";
import { useChildComponentValidator } from "../../../shared/validation";
import { FocusZone } from "../../FocusZone/FocusZone";
import { getDrawerButtonStyles, getDrawerStyles } from "../Drawer.styles";
import { DrawerHeader } from "../DrawerHeader/DrawerHeader";

export type DrawerBorderRadius = "0px" | "4px" | "8px";

export interface DrawerBaseProps extends BaseProps {
    /**
     * Whether the drawer will be open or closed. Defaults to closed.
     */
    readonly drawerOpen?: boolean;
    /**
     * Header element used in the drawer component.
     */
    readonly drawerHeader: React.ReactNode;
    /**
     * Padding surrounding the content of the drawer.
     */
    readonly drawerContentPadding?: string;
    /**
     * Optional borderRadius for the corners of the Drawer.
     * The higher this value, the more rounded the corners are. Defaults to 4px.
     */
    readonly borderRadius?: DrawerBorderRadius;
    /**
     * Optional boolean to toggle the Drawer's boxShadow. Defaults to true
     */
    readonly shadow?: boolean;
    /**
     * OnClick function to run when users are clicking the Drawer to open it.
     * This function runs before the drawer opens.
     */
    readonly onClickToOpen?: (event: React.MouseEvent<HTMLElement>) => void;
    /**
     * OnClick function to run when users are clicking the Drawer to close it.
     * This function runs before the drawer closes.
     */
    readonly onClickToClose?: (event: React.MouseEvent<HTMLElement>) => void;
    /**
     * Optional id for the Drawer button that opens the drawer
     */
    readonly buttonId?: string;
    /**
     * Identifier used in our telemetry when the element is interacted with.
     */
    readonly "data-bi-id"?: string;
    /**
     * Optional boolean to show the divider at the bottom of Drawer. Defaults to false.
     */
    readonly showDivider?: boolean;
    /**
     * Optional boolean to remove parent role="tree". This is used to make Drawer accessible in a List component.
     */
    readonly removeTreeRole?: boolean;
    /**
     * Content to be displayed when the Drawer is opened
     */
    readonly children: React.ReactNode;
}

export interface DrawerContextType {
    readonly drawerOpen: boolean;
    readonly drawerHeaderTitleId?: string;
}

const defaultDrawerContext: DrawerContextType = {
    drawerOpen: false,
    drawerHeaderTitleId: ""
};

export const DrawerContext = createContext<DrawerContextType>(defaultDrawerContext);

// DrawerHeader valid components
export const validDrawerHeaders: readonly React.ComponentType<any>[] = [
    DrawerHeader
];

export const DrawerBase: React.FC<DrawerBaseProps> = ({
    drawerOpen = false,
    drawerHeader,
    drawerContentPadding,
    borderRadius = "4px",
    shadow = false,
    onClickToOpen,
    onClickToClose,
    buttonId,
    children,
    showDivider = false,
    removeTreeRole = false,
    ...baseProps
}) => {
    const theme = useTheme();

    useChildComponentValidator(drawerHeader, validDrawerHeaders, "Drawer");

    const onClickDrawer = (event: React.MouseEvent<HTMLElement>) => {
        const onClickAction = drawerOpen ? onClickToClose : onClickToOpen;
        onClickAction?.(event);
    };

    const onKeyDownDrawer = (event: React.KeyboardEvent<HTMLElement>) => {
        // tslint:disable: no-magic-numbers
        if (event.keyCode === 13 || event.keyCode === 27) {
            onClickDrawer(event as unknown as React.MouseEvent<HTMLElement>);
        }
    };

    const drawerID = `drawer_${generateGuid()}`;
    const drawerHeaderTitleId = `drawerHeaderTitle_${generateGuid()}`;

    return (
        <DrawerContext.Provider value={{ drawerOpen, drawerHeaderTitleId }}>
            <FluentStack
                role={removeTreeRole ? undefined : "tree"}
                styles={getDrawerStyles(theme, borderRadius, shadow, showDivider)}
            >
                <FluentStack horizontal={true}>
                    <FluentStack
                        tabIndex={0}
                        id={buttonId}
                        data-is-focusable={true}
                        onClick={onClickDrawer}
                        onKeyDown={onKeyDownDrawer}
                        styles={getDrawerButtonStyles()}
                        aria-selected={drawerOpen}
                        aria-expanded={drawerOpen}
                        role="treeitem"
                        {...baseProps}
                    >
                        <FocusZone direction="horizontal">
                            {drawerHeader}
                        </FocusZone>
                    </FluentStack>
                </FluentStack>
                {/* tslint:disable-next-line:react-a11y-role-has-required-aria-props */}
                <FluentStack id={drawerID} role={"treeitem"} aria-labelledby={drawerHeaderTitleId} tokens={{ padding: drawerOpen ? drawerContentPadding : "0px" }}>
                    {drawerOpen ? children : undefined}
                </FluentStack>
            </FluentStack>
        </DrawerContext.Provider>
    );
};
